import React, { useState } from 'react';
import { Card } from '../../components/design-system/Card';
import { Button } from '../../components/design-system/Button';
import { clsx } from 'clsx';
import { UserMetrics, getMotivationalMessage } from '../../utils/engagement';

interface PowerUserDashboardProps {
  metrics: UserMetrics | null;
}

const PowerUserDashboard: React.FC<PowerUserDashboardProps> = ({ metrics }) => {
  const [selectedTimeframe, setSelectedTimeframe] = useState<'week' | 'month' | 'year'>('week');

  if (!metrics) return null;

  const motivationalMessage = getMotivationalMessage('power_user', metrics);

  // Advanced metrics for power users
  const advancedMetrics = {
    personalRecords: [
      { exercise: 'Squat', weight: '225 lbs', date: '2 days ago', improvement: '+10 lbs' },
      { exercise: 'Bench Press', weight: '185 lbs', date: '1 week ago', improvement: '+5 lbs' },
      { exercise: 'Deadlift', weight: '315 lbs', date: '3 days ago', improvement: '+15 lbs' },
    ],
    volumeProgression: {
      thisWeek: 42500,
      lastWeek: 38200,
      change: '+11.3%',
    },
    bodyComposition: {
      muscle: { current: 78.5, change: '+2.3 lbs' },
      fat: { current: 15.2, change: '-1.8%' },
      weight: { current: 185, change: '+0.5 lbs' },
    },
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Elite Athlete Dashboard
              </h1>
              <p className="text-gray-600">{motivationalMessage}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm">
                <span className="mr-2">⚙️</span>
                Settings
              </Button>
              <Button variant="primary" size="sm" haptic>
                <span className="mr-2">📊</span>
                Export Data
              </Button>
            </div>
          </div>

          {/* Elite Status Badge */}
          <div className="bg-gradient-to-r from-[#4B5FDC] to-[#00C896] rounded-lg p-1 inline-block">
            <div className="bg-white rounded-md px-4 py-2 flex items-center gap-2">
              <span className="text-2xl">🏆</span>
              <div>
                <div className="text-sm font-semibold text-gray-900">Elite Power User</div>
                <div className="text-xs text-gray-600">{metrics.totalWorkouts}+ workouts completed</div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card variant="highlighted" className="bg-gradient-to-br from-[#4B5FDC]/10 to-[#4B5FDC]/5">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-600 mb-1">Current Streak</p>
                <p className="text-3xl font-bold text-[#4B5FDC]">{metrics.currentStreak} days</p>
                <p className="text-xs text-gray-500 mt-1">Personal best: 45 days</p>
              </div>
              <span className="text-2xl">🔥</span>
            </div>
          </Card>

          <Card variant="highlighted" className="bg-gradient-to-br from-[#00C896]/10 to-[#00C896]/5">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-600 mb-1">Weekly Volume</p>
                <p className="text-3xl font-bold text-[#00C896]">42.5K lbs</p>
                <p className="text-xs text-green-600 mt-1">↑ 11.3% from last week</p>
              </div>
              <span className="text-2xl">💪</span>
            </div>
          </Card>

          <Card variant="highlighted" className="bg-gradient-to-br from-purple-500/10 to-purple-500/5">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-600 mb-1">Consistency Score</p>
                <p className="text-3xl font-bold text-purple-600">98%</p>
                <p className="text-xs text-gray-500 mt-1">Top 5% of athletes</p>
              </div>
              <span className="text-2xl">⭐</span>
            </div>
          </Card>

          <Card variant="highlighted" className="bg-gradient-to-br from-orange-500/10 to-orange-500/5">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-600 mb-1">Recovery Score</p>
                <p className="text-3xl font-bold text-orange-600">8.5/10</p>
                <p className="text-xs text-gray-500 mt-1">Ready for high intensity</p>
              </div>
              <span className="text-2xl">🔋</span>
            </div>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Personal Records & Progress */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Records */}
            <Card>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Recent Personal Records</h2>
                <Button variant="ghost" size="sm">View All PRs</Button>
              </div>
              <div className="space-y-3">
                {advancedMetrics.personalRecords.map((pr, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#4B5FDC]/10 rounded-full flex items-center justify-center">
                        <span className="text-lg">🏋️</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{pr.exercise}</p>
                        <p className="text-sm text-gray-600">{pr.date}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-[#4B5FDC]">{pr.weight}</p>
                      <p className="text-sm text-green-600">{pr.improvement}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Advanced Analytics */}
            <Card>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Performance Analytics</h2>
                <div className="flex gap-2">
                  {(['week', 'month', 'year'] as const).map((timeframe) => (
                    <button
                      key={timeframe}
                      onClick={() => setSelectedTimeframe(timeframe)}
                      className={clsx(
                        'px-3 py-1 text-sm rounded-md transition-all capitalize',
                        selectedTimeframe === timeframe
                          ? 'bg-[#4B5FDC] text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      )}
                    >
                      {timeframe}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Placeholder for advanced charts */}
              <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
                <p className="text-gray-500">Advanced performance charts</p>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">+12%</p>
                  <p className="text-sm text-gray-600">Strength Gain</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">-3.2%</p>
                  <p className="text-sm text-gray-600">Body Fat</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">+8 lbs</p>
                  <p className="text-sm text-gray-600">Muscle Mass</p>
                </div>
              </div>
            </Card>

            {/* Today's Advanced Workout */}
            <Card variant="interactive" className="border-2 border-[#4B5FDC]">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Today's Workout</h2>
                  <p className="text-sm text-gray-600">Push Day - Heavy Volume</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Est. duration</p>
                  <p className="font-semibold text-gray-900">75 min</p>
                </div>
              </div>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-[#4B5FDC]">🎯</span>
                  <span className="text-gray-700">Focus: Progressive Overload</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-[#00C896]">💪</span>
                  <span className="text-gray-700">Volume: 28,500 lbs projected</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-orange-500">🔥</span>
                  <span className="text-gray-700">Intensity: 85-90% 1RM</span>
                </div>
              </div>
              
              <Button variant="primary" className="w-full" haptic celebrateOnClick>
                Start Advanced Workout
              </Button>
            </Card>
          </div>

          {/* Right Column - Body Composition & Community */}
          <div className="space-y-6">
            {/* Body Composition */}
            <Card>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Body Composition</h2>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Muscle Mass</span>
                    <span className="text-sm font-medium text-green-600">{advancedMetrics.bodyComposition.muscle.change}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-[#4B5FDC]" style={{ width: '78.5%' }} />
                    </div>
                    <span className="text-sm font-medium">{advancedMetrics.bodyComposition.muscle.current} lbs</span>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Body Fat</span>
                    <span className="text-sm font-medium text-green-600">{advancedMetrics.bodyComposition.fat.change}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-orange-500" style={{ width: '15.2%' }} />
                    </div>
                    <span className="text-sm font-medium">{advancedMetrics.bodyComposition.fat.current}%</span>
                  </div>
                </div>
                
                <div className="pt-2 border-t">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Total Weight</span>
                    <span className="font-medium">{advancedMetrics.bodyComposition.weight.current} lbs ({advancedMetrics.bodyComposition.weight.change})</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Elite Community */}
            <Card>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Elite Community</h2>
              <div className="space-y-3">
                <div className="p-3 bg-gradient-to-r from-[#4B5FDC]/10 to-[#00C896]/10 rounded-lg">
                  <p className="text-sm font-medium text-gray-900 mb-1">Weekly Challenge</p>
                  <p className="text-xs text-gray-600 mb-2">500 Rep Challenge - Ends in 3 days</p>
                  <div className="flex justify-between items-center">
                    <div className="flex -space-x-2">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="w-6 h-6 bg-gray-300 rounded-full border-2 border-white" />
                      ))}
                      <div className="w-6 h-6 bg-gray-200 rounded-full border-2 border-white flex items-center justify-center">
                        <span className="text-xs">+12</span>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm">Join</Button>
                  </div>
                </div>
                
                <div className="text-center py-4">
                  <p className="text-sm text-gray-600 mb-2">Your Ranking</p>
                  <p className="text-3xl font-bold text-[#4B5FDC]">#42</p>
                  <p className="text-xs text-gray-500">Top 3% globally</p>
                </div>
              </div>
            </Card>

            {/* Advanced Tools */}
            <Card>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Power User Tools</h2>
              <div className="space-y-2">
                <Button variant="ghost" className="w-full justify-start">
                  <span className="mr-3">📈</span>
                  Advanced Analytics
                </Button>
                <Button variant="ghost" className="w-full justify-start">
                  <span className="mr-3">🎯</span>
                  Program Builder
                </Button>
                <Button variant="ghost" className="w-full justify-start">
                  <span className="mr-3">📊</span>
                  Export Training Data
                </Button>
                <Button variant="ghost" className="w-full justify-start">
                  <span className="mr-3">🤝</span>
                  Coach Marketplace
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PowerUserDashboard;